import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  X,
  Loader2,
  AlertCircle,
  Printer,
  FileDown,
  ReceiptText,
  Wallet,
  Banknote,
  Landmark,
  Smartphone,
  ScrollText,
  Calendar,
  Hash,
  User,
  CheckCircle,
  PrinterCheck,
  type LucideIcon,
} from 'lucide-react';
import { usePayoutVoucher } from '../../services/httpServices/queries';
import {
  diagnosticService,
  type DoctorPayment,
  type DiagnosticReceipt,
  type RevenueDistribution,
} from '../../services/httpServices/diagnosticService';
import { printPdfBlobUrl } from '../../utils/printDocument';
import { money, isReferralVoucher, voucherPayeeName } from './payoutShared';
import { PayeeTypeBadge, SourceBadge } from './payoutBadges';
import { cn } from '../../lib/utils';

/** Payment method → icon + label, shown in the voucher summary grid. */
const METHOD_META: Record<string, { label: string; icon: LucideIcon }> = {
  CASH: { label: 'Cash', icon: Banknote },
  BANK: { label: 'Bank', icon: Landmark },
  BKASH: { label: 'bKash', icon: Wallet },
  NAGAD: { label: 'Nagad', icon: Smartphone },
  CHEQUE: { label: 'Cheque', icon: ScrollText },
};

const methodMeta = (method?: string) =>
  METHOD_META[(method || 'CASH').toUpperCase()] || {
    label: method || 'Cash',
    icon: Banknote,
  };

const fmtDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const fmtDateTime = (value?: string | null) =>
  value ? new Date(value).toLocaleString('en-GB') : '—';

function StatusBadge({ status }: { status?: string }) {
  const paid = status === 'PAID';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold',
        paid ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger',
      )}
    >
      {paid && <CheckCircle size={12} />}
      {status || 'PAID'}
    </span>
  );
}

/** One labelled stat in the voucher summary grid. */
function Meta({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-text-muted">
        <Icon size={12} className="shrink-0" />
        {label}
      </p>
      <p className="mt-1 truncate text-sm font-medium text-text" title={typeof value === 'string' ? value : undefined}>
        {value}
      </p>
    </div>
  );
}

/**
 * One row in the Settled items table. Diagnostic accruals are rolled up per
 * receipt (one OPD receipt = one row) so the voucher never lists a commission
 * for every single test — the receipt's share is the sum of its lines.
 */
type SettledRow = {
  key: string;
  kind: 'consultation' | 'admission' | 'diagnostic';
  title: string;
  when?: string | null;
  base: number;
  share: number;
  /** Only shown when the row maps to a single accrual with one rate. */
  percentage?: number;
  appointment?: RevenueDistribution['appointment'];
  receipt?: DiagnosticReceipt;
  testCount?: number;
};

/**
 * Payout voucher details — the modern, read-only drill-down behind a voucher
 * number in the Vouchers ledger. Shows the payee, how the money left the
 * hospital, and the exact settled accrual lines the voucher paid for.
 */
export function PayoutVoucherModal({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) {
  const { data, isLoading, error } = usePayoutVoucher(id);
  const voucher: DoctorPayment | undefined = data?.data;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const referral = voucher ? isReferralVoucher(voucher) : false;
  const lines = useMemo(() => voucher?.distributions ?? [], [voucher]);
  const method = methodMeta(voucher?.method);
  const MethodIcon = method.icon;

  /**
   * Settled accruals, receipt-wise. Consultation and admission lines are
   * already one-per-source, so they pass straight through; diagnostic lines
   * are grouped by their parent OPD receipt and shown as "OPD Receipt" with
   * the receipt number as the reference — no per-test commission breakdown.
   */
  const settledRows = useMemo<SettledRow[]>(() => {
    const rows: SettledRow[] = [];
    const diagnosticIndex = new Map<string, number>();

    for (const d of lines) {
      const base = Number(d.totalAmount) || 0;
      const share = Number(d.doctorShare) || 0;

      if (d.sourceType === 'CONSULTATION') {
        rows.push({
          key: d.id,
          kind: 'consultation',
          title: d.appointment?.appointmentType || 'Consultation',
          when: d.appointment?.date,
          base,
          share,
          percentage: Number(d.doctorPercentage) || 0,
          appointment: d.appointment,
        });
        continue;
      }

      if (d.sourceType === 'ADMISSION') {
        rows.push({
          key: d.id,
          kind: 'admission',
          title: `IPD admission${
            d.admission?.patient
              ? ` — ${[d.admission.patient.firstName, d.admission.patient.lastName]
                  .filter(Boolean)
                  .join(' ')}`
              : ''
          }`,
          when: d.admission?.admittedAt,
          base,
          share,
          percentage: Number(d.doctorPercentage) || 0,
        });
        continue;
      }

      const receipt = d.receiptItem?.receipt;
      const groupKey = receipt?.id || d.receiptItem?.receiptId || d.id;
      const existing = diagnosticIndex.get(groupKey);
      if (existing !== undefined) {
        const row = rows[existing];
        row.base += base;
        row.share += share;
        row.testCount = (row.testCount || 1) + 1;
        continue;
      }

      diagnosticIndex.set(groupKey, rows.length);
      rows.push({
        key: `receipt:${groupKey}`,
        kind: 'diagnostic',
        title: receipt ? 'OPD Receipt' : 'Diagnostic Receipt',
        when: receipt?.createdAt,
        base,
        share,
        receipt,
        testCount: 1,
      });
    }

    return rows;
  }, [lines]);

  const handlePrint = async () => {
    if (!voucher) return;
    try {
      const url = await diagnosticService.printPayoutVoucher(voucher.id);
      await printPdfBlobUrl(url, { onError: (m) => toast.error(m) });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate voucher PDF');
    }
  };

  const handlePdf = async () => {
    if (!voucher) return;
    try {
      const url = await diagnosticService.printPayoutVoucher(voucher.id);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${voucher.paymentNumber || 'payout-voucher'}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to download voucher PDF');
    }
  };

  const preparedBy = voucher?.paidBy
    ? `${voucher.paidBy.firstName || ''} ${voucher.paidBy.lastName || ''}`.trim()
    : '';

  return (
    <div
      className="modal-overlay-enter fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payout-voucher-title"
      onClick={onClose}
    >
      <div
        className="modal-panel-enter flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative shrink-0 border-b border-border">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent" />
          <div className="relative flex items-start gap-3 p-5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <ReceiptText size={20} />
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <h2
                  id="payout-voucher-title"
                  className="truncate font-mono text-lg font-bold tracking-tight text-text"
                >
                  {voucher?.paymentNumber || 'Payout voucher'}
                </h2>
                {voucher?.isOpening && (
                  <span className="inline-flex rounded-full bg-warning/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning">
                    Opening
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
                <span className="inline-flex items-center gap-1">
                  <User size={11} />
                  {voucherPayeeName(voucher ?? ({} as DoctorPayment))}
                </span>
                {voucher && (
                  <PayeeTypeBadge payeeType={referral ? 'REFERRAL' : 'DOCTOR'} />
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="-mr-1 -mt-1 shrink-0 rounded-lg p-1.5 text-text-muted transition-colors hover:bg-background hover:text-text"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-text-muted">
              <Loader2 size={20} className="mr-2 animate-spin text-primary" />
              Loading voucher…
            </div>
          ) : error || !voucher ? (
            <div className="flex items-center gap-2.5 rounded-xl border border-danger/20 bg-danger/10 p-4 text-sm text-danger">
              <AlertCircle size={16} className="shrink-0" />
              <span>Failed to load this payout voucher.</span>
            </div>
          ) : (
            <>
              {/* Amount hero */}
              <div className="rounded-2xl border border-border bg-gradient-to-br from-success/10 via-background/60 to-background/60 p-5">
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
                      Amount paid
                    </p>
                    <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-success">
                      {money(voucher.amount)}
                    </p>
                  </div>
                  <StatusBadge status={voucher.status} />
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4 border-t border-dashed border-border pt-4 sm:grid-cols-4">
                  <Meta icon={MethodIcon} label="Method" value={method.label} />
                  <Meta icon={Calendar} label="Paid on" value={fmtDate(voucher.paidAt)} />
                  <Meta
                    icon={ReceiptText}
                    label="Records"
                    value={`${voucher.distributionCount ?? lines.length} item(s)`}
                  />
                  <Meta icon={User} label="Prepared by" value={preparedBy || '—'} />
                </div>

                {(voucher.externalReference || voucher.notes) && (
                  <div className="mt-4 space-y-1.5 border-t border-dashed border-border pt-4 text-xs text-text-muted">
                    {voucher.externalReference && (
                      <p className="flex items-center gap-1.5">
                        <Hash size={11} />
                        Reference{' '}
                        <span className="font-mono text-text">{voucher.externalReference}</span>
                      </p>
                    )}
                    {voucher.notes && (
                      <p className="whitespace-pre-wrap text-text">{voucher.notes}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Settled line items */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted">
                    <PrinterCheck size={13} className="text-primary" />
                    Settled items
                  </h3>
                  {lines.length > 0 && (
                    <span className="text-xs text-text-muted">{settledRows.length} line(s)</span>
                  )}
                </div>

                {lines.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border bg-background/60 px-4 py-6 text-center text-xs text-text-muted">
                    {voucher.isOpening
                      ? 'Opening-balance voucher — recorded manually, not linked to accruals.'
                      : 'No settled line items recorded for this voucher.'}
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-border">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border bg-background/50 text-left text-[11px] uppercase text-text-muted">
                            <th className="px-3 py-2.5 font-medium">Source</th>
                            <th className="px-3 py-2.5 font-medium">Reference</th>
                            <th className="px-3 py-2.5 font-medium">Date</th>
                            <th className="px-3 py-2.5 text-right font-medium">Base</th>
                            <th className="px-3 py-2.5 text-right font-medium">Share</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {settledRows.map((row) => (
                            <tr key={row.key} className="hover:bg-background/50">
                              <td className="px-3 py-2.5">
                                <p className="font-medium text-text">{row.title}</p>
                                <div className="mt-0.5 flex items-center gap-2">
                                  <SourceBadge
                                    source={
                                      row.kind === 'consultation'
                                        ? 'CONSULTATION'
                                        : row.kind === 'admission'
                                          ? 'ADMISSION'
                                          : 'DIAGNOSTIC'
                                    }
                                  />
                                  {row.kind === 'diagnostic' && (row.testCount || 0) > 1 && (
                                    <span className="text-[10px] text-text-muted">
                                      {row.testCount} test(s)
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-xs">
                                {row.kind === 'consultation' ? (
                                  <span className="text-text-muted">
                                    {row.appointment?.patientName ||
                                      (row.appointment?.patient
                                        ? `${row.appointment.patient.firstName || ''} ${row.appointment.patient.lastName || ''}`.trim()
                                        : '—')}
                                  </span>
                                ) : row.kind === 'admission' ? (
                                  <span className="text-text-muted">Admission</span>
                                ) : row.receipt ? (
                                  <Link
                                    to={`/opd/diagnostic-receipts/${row.receipt.id}`}
                                    className="font-mono text-primary hover:underline"
                                  >
                                    {row.receipt.receiptNumber}
                                  </Link>
                                ) : (
                                  <span className="text-text-muted">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-xs text-text-muted">
                                {fmtDate(row.when)}
                              </td>
                              <td className="px-3 py-2.5 text-right tabular-nums text-text-muted">
                                {money(row.base)}
                              </td>
                              <td className="px-3 py-2.5 text-right">
                                <p className="font-semibold tabular-nums text-primary">
                                  {money(row.share)}
                                </p>
                                {Number(row.percentage) > 0 && (
                                  <p className="text-[10px] text-text-muted">
                                    {Number(row.percentage)}%
                                  </p>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-background/50 px-5 py-4">
          <span className="text-xs text-text-muted">
            {voucher ? `Issued ${fmtDateTime(voucher.createdAt)}` : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2.5 text-sm font-medium text-text-muted transition-colors hover:bg-background hover:text-text"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handlePdf}
              disabled={!voucher}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2.5 text-sm font-medium text-text transition-colors hover:bg-background disabled:opacity-40"
            >
              <FileDown size={15} />
              Download PDF
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={!voucher}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
            >
              <Printer size={15} />
              Print voucher
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
